import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Shield, BarChart3, TrendingUp, Eye, FileText, Zap, Target } from 'lucide-react';

interface CyberOpsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function CyberOpsModal({ isOpen, onClose }: CyberOpsModalProps) {
  const [activeTab, setActiveTab] = useState('overview');

  if (!isOpen) return null;

  const tabs = [
    { id: 'overview', label: 'Vue d\'ensemble', icon: Eye },
    { id: 'features', label: 'Fonctionnalités', icon: Zap },
    { id: 'visualizations', label: 'Visualisations', icon: BarChart3 },
    { id: 'integration', label: 'Intégrations', icon: Target },
  ];

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="bg-gradient-to-br from-slate-900 via-indigo-900 to-slate-900 rounded-2xl max-w-6xl w-full my-8 border border-indigo-500/30 shadow-2xl shadow-indigo-500/20"
          >
            {/* Header */}
            <div className="relative p-8 border-b border-indigo-500/30">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-gradient-to-br from-cyan-500 to-blue-500 rounded-xl">
                    <BarChart3 className="w-10 h-10 text-white" />
                  </div>
                  <div>
                    <h2 className="text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-400">
                      CyberOps
                    </h2>
                    <p className="text-gray-400 mt-1 text-lg">
                      Outil propriétaire BZ Inc. - Visualisation et documentation de la posture cybersécurité
                    </p>
                  </div>
                </div>
                <button
                  onClick={onClose}
                  className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                >
                  <X className="w-6 h-6 text-gray-400" />
                </button>
              </div>
            </div>

            {/* Tabs */}
            <div className="flex overflow-x-auto border-b border-indigo-500/30 bg-slate-900/50">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center gap-2 px-6 py-4 font-semibold whitespace-nowrap transition-all ${
                      activeTab === tab.id
                        ? 'bg-gradient-to-r from-cyan-600 to-blue-600 text-white border-b-2 border-cyan-400'
                        : 'text-gray-400 hover:text-white hover:bg-white/5'
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                    {tab.label}
                  </button>
                );
              })}
            </div>

            {/* Content */}
            <div className="p-8 space-y-8 max-h-[calc(100vh-300px)] overflow-y-auto">
              
              {/* TAB: Vue d'ensemble */}
              {activeTab === 'overview' && (
                <div className="space-y-8">
                  <div className="bg-gradient-to-r from-cyan-600/20 via-blue-600/20 to-indigo-600/20 rounded-xl p-8 border-2 border-cyan-500/50">
                    <h3 className="text-3xl font-bold text-center text-white mb-4">
                      CyberOps : Notre Fierté
                    </h3>
                    <p className="text-gray-300 text-lg text-center leading-relaxed">
                      <strong className="text-white">CyberOps</strong> est un outil développé à l'interne par BZ Inc. 
                      pour <strong className="text-cyan-400">visualiser</strong>, <strong className="text-blue-400">documenter</strong> et 
                      <strong className="text-indigo-400"> analyser</strong> la posture cybersécurité de nos clients.
                    </p>
                    <p className="text-gray-300 text-lg text-center leading-relaxed mt-4">
                      Avec des <strong className="text-white">graphiques avancés</strong>, des <strong className="text-white">spider charts</strong>, 
                      et des <strong className="text-white">visualisations en temps réel</strong>, CyberOps transforme les données cybersécurité 
                      en informations claires et actionnables.
                    </p>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="bg-gradient-to-br from-cyan-500/10 to-blue-500/10 rounded-xl p-6 border border-cyan-500/30">
                      <h4 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
                        <Eye className="w-7 h-7 text-cyan-400" />
                        Vision Claire
                      </h4>
                      <p className="text-gray-300 mb-4">
                        CyberOps vous donne une <strong className="text-white">vision claire et complète</strong> de votre posture cybersécurité. 
                        Plus de données brutes incompréhensibles. Des visualisations qui parlent.
                      </p>
                      <ul className="space-y-2 text-gray-300">
                        <li className="flex items-start gap-2">
                          <span className="text-cyan-400">✓</span>
                          <span>Posture cybersécurité en temps réel</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-cyan-400">✓</span>
                          <span>Graphiques spider pour analyse multidimensionnelle</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-cyan-400">✓</span>
                          <span>Comparaisons historiques et tendances</span>
                        </li>
                      </ul>
                    </div>

                    <div className="bg-gradient-to-br from-blue-500/10 to-indigo-500/10 rounded-xl p-6 border border-blue-500/30">
                      <h4 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
                        <FileText className="w-7 h-7 text-blue-400" />
                        Documentation Professionnelle
                      </h4>
                      <p className="text-gray-300 mb-4">
                        CyberOps <strong className="text-white">documente automatiquement</strong> votre posture cybersécurité. 
                        Rapports professionnels, analyses détaillées, recommandations actionnables.
                      </p>
                      <ul className="space-y-2 text-gray-300">
                        <li className="flex items-start gap-2">
                          <span className="text-blue-400">✓</span>
                          <span>Rapports automatisés et personnalisables</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-blue-400">✓</span>
                          <span>Documentation conforme aux standards</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-blue-400">✓</span>
                          <span>Export dans différents formats</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              )}

              {/* TAB: Fonctionnalités */}
              {activeTab === 'features' && (
                <div className="space-y-6">
                  <div className="text-center mb-8">
                    <h3 className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-400 mb-3">
                      Fonctionnalités Principales
                    </h3>
                  </div>

                  <div className="space-y-6">
                    {[
                      {
                        title: 'Dashboard de Posture',
                        description: 'Vue d\'ensemble complète de votre posture cybersécurité avec indicateurs clés et métriques essentielles.',
                        icon: Shield,
                        bgClass: 'from-cyan-500/10 to-cyan-600/10',
                        borderClass: 'border-cyan-500/30',
                        iconBg: 'bg-cyan-500/20',
                        iconText: 'text-cyan-400'
                      },
                      {
                        title: 'Graphiques Spider',
                        description: 'Analyse multidimensionnelle de votre posture avec des graphiques spider pour visualiser les forces et faiblesses.',
                        icon: Target,
                        bgClass: 'from-blue-500/10 to-blue-600/10',
                        borderClass: 'border-blue-500/30',
                        iconBg: 'bg-blue-500/20',
                        iconText: 'text-blue-400'
                      },
                      {
                        title: 'Tendances et Évolution',
                        description: 'Suivi de l\'évolution de votre posture cybersécurité dans le temps avec graphiques de tendances et comparaisons historiques.',
                        icon: TrendingUp,
                        bgClass: 'from-indigo-500/10 to-indigo-600/10',
                        borderClass: 'border-indigo-500/30',
                        iconBg: 'bg-indigo-500/20',
                        iconText: 'text-indigo-400'
                      },
                      {
                        title: 'Rapports Personnalisés',
                        description: 'Génération automatique de rapports professionnels adaptés à vos besoins et à vos audiences.',
                        icon: FileText,
                        bgClass: 'from-purple-500/10 to-purple-600/10',
                        borderClass: 'border-purple-500/30',
                        iconBg: 'bg-purple-500/20',
                        iconText: 'text-purple-400'
                      },
                    ].map((feature, index) => {
                      const Icon = feature.icon;
                      return (
                        <div key={index} className={`bg-gradient-to-br ${feature.bgClass} rounded-xl p-6 border ${feature.borderClass}`}>
                          <div className="flex items-start gap-4">
                            <div className={`p-3 ${feature.iconBg} rounded-lg`}>
                              <Icon className={`w-8 h-8 ${feature.iconText}`} />
                            </div>
                            <div className="flex-1">
                              <h4 className="text-xl font-bold text-white mb-2">{feature.title}</h4>
                              <p className="text-gray-300">{feature.description}</p>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* TAB: Visualisations */}
              {activeTab === 'visualizations' && (
                <div className="space-y-8">
                  <div className="bg-gradient-to-r from-cyan-600/20 via-blue-600/20 to-indigo-600/20 rounded-xl p-8 border-2 border-cyan-500/50">
                    <h3 className="text-2xl font-bold text-center text-white mb-4">
                      Visualisations Avancées
                    </h3>
                    <p className="text-gray-300 text-center text-lg">
                      CyberOps transforme vos données cybersécurité en visualisations claires et actionnables
                    </p>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="bg-slate-800/50 rounded-lg p-6 border border-cyan-500/30">
                      <h4 className="text-xl font-bold text-white mb-3">📊 Graphiques Spider</h4>
                      <p className="text-gray-300 mb-4">
                        Analyse multidimensionnelle de votre posture cybersécurité. Visualisez simultanément 
                        plusieurs dimensions (GOVERN, IDENTIFY, PROTECT, DETECT, RESPOND, RECOVER) sur un même graphique.
                      </p>
                      <p className="text-gray-300">
                        <strong className="text-cyan-400">Parfait pour:</strong> Présentations exécutives, 
                        identification rapide des zones à améliorer, comparaisons entre périodes.
                      </p>
                    </div>

                    <div className="bg-slate-800/50 rounded-lg p-6 border border-blue-500/30">
                      <h4 className="text-xl font-bold text-white mb-3">📈 Graphiques de Tendances</h4>
                      <p className="text-gray-300 mb-4">
                        Suivez l'évolution de votre posture cybersécurité dans le temps. Graphiques linéaires, 
                        barres, aires pour visualiser les progrès et les tendances.
                      </p>
                      <p className="text-gray-300">
                        <strong className="text-blue-400">Parfait pour:</strong> Démonstration de progrès, 
                        justification d'investissements, reporting régulier.
                      </p>
                    </div>

                    <div className="bg-slate-800/50 rounded-lg p-6 border border-indigo-500/30">
                      <h4 className="text-xl font-bold text-white mb-3">🎯 Indicateurs Clés</h4>
                      <p className="text-gray-300 mb-4">
                        Tableaux de bord avec indicateurs clés de performance (KPI) cybersécurité. 
                        Métriques essentielles en un coup d'œil.
                      </p>
                      <p className="text-gray-300">
                        <strong className="text-indigo-400">Parfait pour:</strong> Monitoring quotidien, 
                        alertes précoces, prise de décision rapide.
                      </p>
                    </div>

                    <div className="bg-slate-800/50 rounded-lg p-6 border border-purple-500/30">
                      <h4 className="text-xl font-bold text-white mb-3">🔍 Analyses Comparatives</h4>
                      <p className="text-gray-300 mb-4">
                        Comparez votre posture avec des benchmarks, des périodes précédentes, ou des objectifs cibles. 
                        Visualisations côte à côte pour analyse rapide.
                      </p>
                      <p className="text-gray-300">
                        <strong className="text-purple-400">Parfait pour:</strong> Évaluation de performance, 
                        identification de gaps, planification stratégique.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* TAB: Intégrations */}
              {activeTab === 'integration' && (
                <div className="space-y-8">
                  <div className="bg-gradient-to-r from-cyan-600/20 via-blue-600/20 to-indigo-600/20 rounded-xl p-8 border-2 border-cyan-500/50">
                    <h3 className="text-2xl font-bold text-center text-white mb-4">
                      Intégrations et Sources de Données
                    </h3>
                    <p className="text-gray-300 text-center text-lg">
                      CyberOps intègre toutes vos sources de données cybersécurité pour une vision unifiée
                    </p>
                  </div>

                  <div className="space-y-6">
                    <div className="bg-gradient-to-br from-blue-500/10 to-cyan-500/10 rounded-xl p-6 border border-blue-500/30">
                      <h4 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
                        <Shield className="w-7 h-7 text-blue-400" />
                        Microsoft 365 & Office 365
                      </h4>
                      <p className="text-gray-300 mb-4">
                        CyberOps intègre directement avec <strong className="text-white">Microsoft 365</strong> et 
                        <strong className="text-white"> Office 365</strong> (Premium/E5) pour analyser votre posture sécuritaire.
                      </p>
                      <ul className="space-y-2 text-gray-300">
                        <li className="flex items-start gap-2">
                          <span className="text-blue-400">✓</span>
                          <span>Analyse de la configuration sécuritaire Microsoft 365</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-blue-400">✓</span>
                          <span>Évaluation de la posture Defender, Compliance, Purview</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-blue-400">✓</span>
                          <span>Monitoring des alertes et incidents de sécurité</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-blue-400">✓</span>
                          <span>Analyse de conformité et gouvernance des données</span>
                        </li>
                      </ul>
                    </div>

                    <div className="bg-gradient-to-br from-indigo-500/10 to-purple-500/10 rounded-xl p-6 border border-indigo-500/30">
                      <h4 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
                        <Target className="w-7 h-7 text-indigo-400" />
                        Autres Sources de Données
                      </h4>
                      <p className="text-gray-300 mb-4">
                        CyberOps peut intégrer d'autres sources de données cybersécurité pour une vision complète.
                      </p>
                      <ul className="space-y-2 text-gray-300">
                        <li className="flex items-start gap-2">
                          <span className="text-indigo-400">✓</span>
                          <span>Outils de sécurité tiers (firewalls, antivirus, SIEM)</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-indigo-400">✓</span>
                          <span>Résultats d'audits et d'évaluations</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-indigo-400">✓</span>
                          <span>Données de conformité (Loi 25, ISO 27001, etc.)</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-indigo-400">✓</span>
                          <span>Métriques personnalisées et KPIs</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              )}

            </div>

            {/* Footer */}
            <div className="p-6 border-t border-indigo-500/30 bg-slate-900/50">
              <div className="flex items-center justify-end">
                <button
                  onClick={onClose}
                  className="px-6 py-3 bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-xl hover:from-cyan-600 hover:to-blue-600 transition-all duration-200 font-semibold shadow-lg shadow-cyan-500/20 hover:shadow-cyan-500/40 hover:scale-105"
                >
                  Fermer
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}

